import type { InfiniteData } from '@tanstack/react-query';
import type { PostData } from '@dove/shared';
import type { FeedPage } from '@/lib/api';
import { keys, queryClient } from '@/lib/query-client';

type SpacePage = { posts: PostData[] };
type ProfilePage = { posts: PostData[] };
type LetterPage = { post: PostData; comments: PostData[] };

/** Every query family that can contain a PostData card. */
const postQueryPrefixes = [keys.feed, ['space'] as const, keys.profiles, ['letter'] as const];

function updatePosts(
  posts: PostData[],
  id: string,
  update: (post: PostData) => PostData,
): PostData[] {
  const at = posts.findIndex((post) => post.id === id);
  if (at < 0) return posts;
  const next = posts.slice();
  next[at] = update(posts[at]!);
  return next;
}

function removePost(posts: PostData[], id: string): PostData[] {
  return posts.some((post) => post.id === id)
    ? posts.filter((post) => post.id !== id)
    : posts;
}

/** Patch one card everywhere it is currently cached, preserving untouched refs. */
export function updatePostCaches(
  id: string,
  update: (post: PostData) => PostData,
): void {
  queryClient.setQueryData<InfiniteData<FeedPage, string>>(keys.feed, (old) => {
    if (!old) return old;
    let changed = false;
    const pages = old.pages.map((page) => {
      const groups = page.groups.map((group) => {
        const posts = updatePosts(group.posts, id, update);
        if (posts === group.posts) return group;
        changed = true;
        return { ...group, posts };
      });
      return groups.some((group, index) => group !== page.groups[index])
        ? { ...page, groups }
        : page;
    });
    return changed ? { ...old, pages } : old;
  });

  queryClient.setQueriesData<SpacePage>({ queryKey: ['space'] }, (old) => {
    if (!old) return old;
    const posts = updatePosts(old.posts, id, update);
    return posts === old.posts ? old : { ...old, posts };
  });

  queryClient.setQueriesData<ProfilePage>({ queryKey: keys.profiles }, (old) => {
    if (!old) return old;
    const posts = updatePosts(old.posts, id, update);
    return posts === old.posts ? old : { ...old, posts };
  });

  queryClient.setQueriesData<LetterPage>({ queryKey: ['letter'] }, (old) => {
    if (!old) return old;
    if (old.post.id === id) return { ...old, post: update(old.post) };
    const comments = updatePosts(old.comments, id, update);
    return comments === old.comments ? old : { ...old, comments };
  });
}

function prependUnique(posts: PostData[], post: PostData): PostData[] {
  return posts.some((cached) => cached.id === post.id) ? posts : [post, ...posts];
}

/**
 * Put a server-confirmed send directly into every list that already exists.
 *
 * Top-level letters enter the feed; comments do not. Both enter their room and
 * author's profile, and a comment is appended to its chronological conversation.
 */
export function insertPostCaches(post: PostData): void {
  if (!post.replyToId) {
    queryClient.setQueryData<InfiniteData<FeedPage, string>>(keys.feed, (old) => {
      if (!old || old.pages.length === 0) return old;
      if (old.pages.some((page) => page.groups.some((group) => group.id === post.id))) {
        return old;
      }
      const pages = old.pages.slice();
      pages[0] = {
        ...pages[0]!,
        groups: [{ id: post.id, posts: [post] }, ...pages[0]!.groups],
      };
      return { ...old, pages };
    });
  }

  queryClient.setQueryData<SpacePage>(keys.space(post.space.slug), (old) => {
    if (!old) return old;
    const posts = prependUnique(old.posts, post);
    return posts === old.posts ? old : { ...old, posts };
  });

  queryClient.setQueryData<ProfilePage>(keys.profile(post.author.handle), (old) => {
    if (!old) return old;
    const posts = prependUnique(old.posts, post);
    return posts === old.posts ? old : { ...old, posts };
  });

  if (post.replyToId) {
    queryClient.setQueriesData<LetterPage>({ queryKey: ['letter'] }, (old) => {
      if (!old || old.post.id !== post.replyToId) return old;
      if (old.comments.some((comment) => comment.id === post.id)) return old;
      return {
        ...old,
        post: { ...old.post, hasComments: true, commentsSeen: true },
        comments: [...old.comments, post],
      };
    });
    updatePostCaches(post.replyToId, (parent) => ({
      ...parent,
      hasComments: true,
      commentsSeen: true,
    }));
  }
}

/** Remove a withdrawn card without re-reading every list that contained it. */
export function removePostCaches(
  id: string,
  result: { parentId?: string; hasComments?: boolean } = {},
): void {
  queryClient.setQueryData<InfiniteData<FeedPage, string>>(keys.feed, (old) => {
    if (!old) return old;
    let changed = false;
    const pages = old.pages.map((page) => {
      const groups = page.groups.flatMap((group) => {
        const posts = removePost(group.posts, id);
        if (posts === group.posts) return [group];
        changed = true;
        return posts.length ? [{ ...group, posts }] : [];
      });
      return groups.length === page.groups.length &&
        groups.every((group, index) => group === page.groups[index])
        ? page
        : { ...page, groups };
    });
    return changed ? { ...old, pages } : old;
  });

  queryClient.setQueriesData<SpacePage>({ queryKey: ['space'] }, (old) => {
    if (!old) return old;
    const posts = removePost(old.posts, id);
    return posts === old.posts ? old : { ...old, posts };
  });
  queryClient.setQueriesData<ProfilePage>({ queryKey: keys.profiles }, (old) => {
    if (!old) return old;
    const posts = removePost(old.posts, id);
    return posts === old.posts ? old : { ...old, posts };
  });

  for (const [queryKey, old] of queryClient.getQueriesData<LetterPage>({
    queryKey: ['letter'],
  })) {
    if (!old) continue;
    if (old.post.id === id) {
      /* A deleted root must resolve through the endpoint once more to become the
         screen's ordinary 404 state; this is one exact read, never a list sweep. */
      void queryClient.invalidateQueries({ queryKey, exact: true });
      continue;
    }
    const comments = removePost(old.comments, id);
    if (comments !== old.comments) {
      queryClient.setQueryData<LetterPage>(queryKey, {
        ...old,
        comments,
        post:
          old.post.id === result.parentId && result.hasComments !== undefined
            ? {
                ...old.post,
                hasComments: result.hasComments || undefined,
                commentsSeen: result.hasComments ? old.post.commentsSeen : undefined,
              }
            : old.post,
      });
    }
  }

  if (result.parentId && result.hasComments !== undefined) {
    updatePostCaches(result.parentId, (parent) => ({
      ...parent,
      hasComments: result.hasComments || undefined,
      commentsSeen: result.hasComments ? parent.commentsSeen : undefined,
    }));
  }
}

/** Stop a refetch from overwriting an optimistic card while its write is in flight. */
export async function cancelPostQueries(): Promise<void> {
  await Promise.all(
    postQueryPrefixes.map((queryKey) =>
      queryClient.cancelQueries({ queryKey }, { silent: true }),
    ),
  );
}

/**
 * Re-read every list that can hold a card, for a write this module cannot patch.
 *
 * THE SAME FOUR PREFIXES `cancelPostQueries` USES, and that is the point: which
 * query families contain a `PostData` is this module's knowledge, and a caller that
 * restates it as four exact keys drifts the moment a fifth family appears — and
 * misses the aliases in the first place. `keys.letter` is keyed by the LETTER's id
 * but the composer caches a payload under the COMMENT id it was addressed with, so
 * an exact-key invalidation leaves a second copy of the same conversation stale.
 *
 * It is not as blunt as it reads: `invalidateQueries` refetches ACTIVE queries only,
 * and one screen is mounted. Everything else is merely marked stale, which is what
 * makes this the older, slower, still correct answer to a write whose projection the
 * server did not return (docs/5 § D260806-1241).
 */
export function invalidatePostCaches(): void {
  for (const queryKey of postQueryPrefixes) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
